/**
 * URL State Management Utilities
 * R2-bundle safe - uses window APIs only, no Next.js hooks
 */

// Type definitions
export type URLStateValue = string | number | boolean | null | undefined;
export type URLStateObject = Record<string, URLStateValue | URLStateValue[] | Record<string, URLStateValue>>;

export interface URLStateOptions {
  /** Debounce URL updates (ms). Default: 0 (immediate) */
  debounce?: number;
  /** Replace vs push history. Default: 'replace' */
  historyMode?: 'replace' | 'push';
  /** Prefix for all params. Default: '' */
  prefix?: string;
}

// ============================================================================
// ENCODING
// ============================================================================

/**
 * Encode a state object to URLSearchParams
 * Handles: strings, numbers, booleans, arrays, nested objects
 */
export function encodeState(state: URLStateObject): URLSearchParams {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(state)) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      // Arrays: JSON-encode
      params.set(key, JSON.stringify(value));
    } else if (typeof value === 'object') {
      // Objects: JSON-encode
      params.set(key, JSON.stringify(value));
    } else if (typeof value === 'boolean') {
      // Booleans: string 'true'/'false'
      params.set(key, value ? 'true' : 'false');
    } else {
      // Strings/numbers: direct conversion
      params.set(key, String(value));
    }
  }

  return params;
}

/**
 * Encode state to a query string (without leading ?)
 */
export function encodeStateToString(state: URLStateObject): string {
  return encodeState(state).toString();
}

// ============================================================================
// DECODING
// ============================================================================

export interface DecodeOptions<T> {
  /** Schema defining expected types for each key */
  schema: {
    [K in keyof T]: 'string' | 'number' | 'boolean' | 'array' | 'object';
  };
  /** Default values for missing keys */
  defaults?: Partial<T>;
}

/**
 * Decode URLSearchParams to typed state object
 */
export function decodeState<T extends URLStateObject>(
  params: URLSearchParams,
  options: DecodeOptions<T>
): Partial<T> {
  const result: Record<string, unknown> = {};

  for (const [key, type] of Object.entries(options.schema)) {
    const value = params.get(key);

    if (value === null) {
      if (options.defaults && key in options.defaults) {
        result[key] = options.defaults[key as keyof T];
      }
      continue;
    }

    switch (type) {
      case 'string':
        result[key] = value;
        break;
      case 'number':
        const num = parseFloat(value);
        result[key] = isNaN(num) ? options.defaults?.[key as keyof T] : num;
        break;
      case 'boolean':
        result[key] = value === 'true';
        break;
      case 'array':
      case 'object':
        try {
          result[key] = JSON.parse(value);
        } catch {
          result[key] = options.defaults?.[key as keyof T];
        }
        break;
    }
  }

  return result as Partial<T>;
}

/**
 * Decode from current window.location.search
 * R2-bundle safe
 */
export function decodeCurrentURL<T extends URLStateObject>(
  options: DecodeOptions<T>
): Partial<T> {
  if (typeof window === 'undefined') {
    return (options.defaults || {}) as Partial<T>;
  }
  return decodeState(new URLSearchParams(window.location.search), options);
}

// ============================================================================
// URL UPDATES
// ============================================================================

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Update the current URL with new state
 * R2-bundle safe - uses window.history.replaceState
 */
export function updateURL(
  state: URLStateObject,
  options: URLStateOptions = {}
): void {
  if (typeof window === 'undefined') return;

  const { debounce = 0, historyMode = 'replace', prefix = '' } = options;

  const doUpdate = () => {
    let params = encodeState(state);

    // Apply prefix if provided
    if (prefix) {
      const prefixedParams = new URLSearchParams();
      params.forEach((value, key) => {
        prefixedParams.set(`${prefix}${key}`, value);
      });
      params = prefixedParams;
    }

    const queryString = params.toString();
    const newURL = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    try {
      // Preserve existing history state (Next.js stores router state in history.state)
      const existingState = window.history.state;
      if (historyMode === 'push') {
        window.history.pushState(existingState, '', newURL);
      } else {
        window.history.replaceState(existingState, '', newURL);
      }
    } catch (e) {
      console.warn('[url-state] Failed to update URL:', e);
    }
  };

  if (debounce > 0) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(doUpdate, debounce);
  } else {
    doUpdate();
  }
}

/**
 * Merge new state with existing URL state
 */
export function mergeURLState(
  newState: Partial<URLStateObject>,
  options: URLStateOptions = {}
): void {
  if (typeof window === 'undefined') return;

  const currentParams = new URLSearchParams(window.location.search);
  const merged: URLStateObject = {};

  // Copy existing params
  currentParams.forEach((value, key) => {
    merged[key] = value;
  });

  // Merge new state
  for (const [key, value] of Object.entries(newState)) {
    if (value === undefined || value === null) {
      delete merged[key];
    } else {
      merged[key] = value as URLStateValue;
    }
  }

  updateURL(merged, options);
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Get current URL search params (R2-bundle safe)
 */
export function getSearchParams(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

/**
 * Check if a specific param exists in current URL
 */
export function hasParam(key: string): boolean {
  return getSearchParams().has(key);
}

/**
 * Get a single param value with type coercion
 */
export function getParam<T extends URLStateValue>(
  key: string,
  type: 'string' | 'number' | 'boolean',
  defaultValue?: T
): T | undefined {
  const value = getSearchParams().get(key);
  if (value === null) return defaultValue;

  switch (type) {
    case 'string':
      return value as T;
    case 'number':
      const num = parseFloat(value);
      return (isNaN(num) ? defaultValue : num) as T;
    case 'boolean':
      return (value === 'true') as T;
    default:
      return value as T;
  }
}

/**
 * Clear all URL params
 */
export function clearURL(): void {
  if (typeof window === 'undefined') return;
  try {
    window.history.replaceState(window.history.state, '', window.location.pathname);
  } catch (e) {
    console.warn('[url-state] Failed to clear URL:', e);
  }
}
